import { apiClient } from '@/lib/api';
import axiosInstance from '@/lib/api/axios-instance';
import { ENDPOINTS } from '@/lib/api/endpoints';

/**
 * La carga manual de precios de competencia (spec 199 §3.1 y §4).
 *
 * Vive en Precios Dinámicos → Competidores porque la carga es por tienda y la
 * tienda vive ahí. Es la corrección de la razón 3 del §1: estaba en el menú
 * equivocado, y por eso nunca se usó.
 */

export type RechazoDeFila = { row: number; field: string; message: string };

export type ResultadoDeCarga = {
  jobId: string;
  guardados: number;
  reconocidos: number;
  rechazados: number;
  sinDecidir: number;
  /** Cuántos precios YA llegaron a la pantalla de Productos */
  preciosAterrizados: number;
  /** Cuántos se reconocieron pero esperan la inferencia para llegar a un precio */
  esperandoInferencia: number;
  rechazos: RechazoDeFila[];
};

export type Candidato = {
  externalId: string;
  nombre: string;
  confianza: number;
  /** Es un renglón de una carga manual anterior, no un artículo de la tienda */
  deCargaManual: boolean;
};

export type EstadoDeRenglon = 'RECONOCIDO' | 'SIN_DECIDIR' | 'NUEVO_MARCADO' | 'RECHAZADO';

export type RenglonDelPanel = {
  id: string;
  nombre: string;
  marca: string | null;
  categoria: string | null;
  precio: number;
  estado: EstadoDeRenglon | null;
  /** Identificado en la tienda NO es lo mismo que vinculado a un producto nuestro */
  vinculadoAProducto: boolean;
  motivoRechazo: string | null;
  ignorado: boolean;
  confianza: number;
  precioAnterior: number | null;
  variacion: number | null;
  candidatos: Candidato[];
};

export type PanelDeIdentificacion = {
  job: {
    id: string;
    tienda: { id: string; name: string };
    fuente: string;
    estado: 'PENDIENTE' | 'LISTA';
  };
  renglones: RenglonDelPanel[];
  contadores: {
    total: number;
    reconocidos: number;
    sinDecidir: number;
    nuevos: number;
    rechazados: number;
    ignorados: number;
  };
  puedeCerrarse: boolean;
  paginacion: { page: number; limit: number; total: number };
};

export type DecisionDeFila =
  | { tipo: 'CONFIRMAR'; externalIdElegido?: string }
  | { tipo: 'MARCAR_NUEVO' }
  | { tipo: 'IGNORAR' }
  | { tipo: 'RECHAZAR'; motivo: string };

export type FiltrosDelPanel = {
  estado?: string;
  busqueda?: string;
  confianzaMin?: number;
  confianzaMax?: number;
};

/**
 * 🔴 Va por `axiosInstance` y NO por `apiClient`.
 *
 * `apiClient.get` devuelve `response.data.data`, y con `responseType: 'blob'`
 * la respuesta ES el Blob: `.data` sobre un Blob da `undefined`. Es la misma
 * trampa que rompió la carga masiva del Pricer. `admin-service.ts` usa esta
 * misma salida por una razón parecida.
 */
export async function bajarPlantilla(storeId: string, nombreTienda: string): Promise<void> {
  const respuesta = await axiosInstance.get(ENDPOINTS.CARGA_MANUAL_PLANTILLA(storeId), {
    responseType: 'blob',
  });

  const url = URL.createObjectURL(respuesta.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `precios-${nombreTienda.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function subirCargaManual(storeId: string, archivo: File): Promise<ResultadoDeCarga> {
  const form = new FormData();
  form.append('file', archivo);

  return apiClient.post<ResultadoDeCarga>(ENDPOINTS.CARGA_MANUAL_SUBIR(storeId), form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

export async function traerPanel(
  jobId: string,
  filtros: FiltrosDelPanel = {}
): Promise<PanelDeIdentificacion> {
  return apiClient.get<PanelDeIdentificacion>(ENDPOINTS.CARGA_MANUAL_PANEL(jobId), {
    params: filtros,
  });
}

/** Un artículo del catálogo de la tienda, buscado a mano (§4.1). */
export type ArticuloDeTienda = Candidato & { yaVinculado: boolean };

/**
 * Busca en TODO el catálogo de esa tienda, no entre los candidatos que
 * propusimos.
 *
 * 🔴 El buscador anterior filtraba los 5 candidatos que el puntaje había
 * elegido, así que un artículo con 15 % —o el sexto mejor— era imposible de
 * elegir. La decisión de una persona no puede quedar limitada por nuestro
 * puntaje: si alguien decide que es ése, es ése.
 */
export async function buscarArticulos(
  storeId: string,
  busqueda: string,
  contra: string
): Promise<ArticuloDeTienda[]> {
  return apiClient.get<ArticuloDeTienda[]>(ENDPOINTS.CARGA_MANUAL_ARTICULOS(storeId), {
    params: { busqueda, contra },
  });
}

export async function decidirFila(itemId: string, decision: DecisionDeFila): Promise<void> {
  await apiClient.put(ENDPOINTS.CARGA_MANUAL_DECIDIR(itemId), decision);
}

export async function decidirEnBloque(
  jobId: string,
  cuerpo: { desdeConfianza?: number; itemIds?: string[]; decision: DecisionDeFila }
): Promise<{ aplicados: number; fallidos: { id: string; motivo: string }[] }> {
  return apiClient.post(ENDPOINTS.CARGA_MANUAL_BLOQUE(jobId), cuerpo);
}

/**
 * Devuelve `cerrada: false` cuando el backend se negó porque quedan renglones
 * sin decidir (§4.2). No es un error: es la respuesta esperada, y la pantalla
 * la usa para mostrar la advertencia con el número exacto.
 */
export async function cerrarCarga(
  jobId: string,
  aceptaNuevos: boolean
): Promise<{ cerrada: boolean; sinDecidir: number }> {
  try {
    await apiClient.post(ENDPOINTS.CARGA_MANUAL_CERRAR(jobId), { aceptaNuevos });
    return { cerrada: true, sinDecidir: 0 };
  } catch (error) {
    const respuesta = (
      error as { response?: { status?: number; data?: { data?: { sinDecidir?: number } } } }
    ).response;
    if (respuesta?.status === 409) {
      return { cerrada: false, sinDecidir: respuesta.data?.data?.sinDecidir ?? 0 };
    }
    throw error;
  }
}
